import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  TemplateRef,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from "@angular/core";

import { BaseChipDirective } from "../chips/shared/base-chip.directive";
import { ChipContentComponent } from "../chips/shared/chip-content.component";
import { OverflowItemDirective } from "../overflow-list";
import { VowkeyIcon } from "../shared/icon";
import { TooltipDirective } from "../tooltip";

import {
  FILTER_CONTROL,
  FILTER_HOST,
  FILTER_PRESENTER,
  FilterControl,
  FilterPresenter,
  FilterSelection,
} from "./filter-tokens";

/**
 * A single on/off filter chip — no menu. Its value is a boolean.
 *
 * @example
 * ```html
 * <bit-filter-toggle
 *   key="favorites"
 *   label="Favorites"
 *   icon="bwi-star"
 *   iconActive="bwi-star-f"
 * ></bit-filter-toggle>
 * ```
 */
@Component({
  selector: "bit-filter-toggle",
  templateUrl: "./filter-toggle.component.html",
  imports: [ChipContentComponent, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: FILTER_CONTROL, useExisting: forwardRef(() => FilterToggleComponent) },
    { provide: FILTER_PRESENTER, useExisting: forwardRef(() => FilterToggleComponent) },
  ],
  hostDirectives: [
    { directive: BaseChipDirective, inputs: ["disabled", "size", "fullWidth"] },
    // Lets a `bitOverflowList` ancestor measure the chip; inert with no such ancestor.
    OverflowItemDirective,
  ],
})
export class FilterToggleComponent implements FilterControl, FilterPresenter, OnInit {
  /** The chip's key — the property its boolean value occupies in the host's `filterValues`. */
  readonly key = input.required<string>();

  /** The chip's label. */
  readonly label = input.required<string>();

  /** Leading icon, shown in both states unless {@link iconActive} overrides it while active. */
  readonly icon = input<VowkeyIcon>();

  /**
   * Icon shown while active — e.g. `bwi-star-f` for an outline `bwi-star`. Falls
   * back to {@link icon} when omitted.
   */
  readonly iconActive = input<VowkeyIcon>();

  /**
   * Tooltip text to explain why the chip is disabled. The chip carries no tooltip otherwise, so
   * this is the only one it shows, and only while {@link disabled} is true. Pass an
   * already-localized string.
   */
  readonly disabledTooltip = input("");

  protected readonly baseChip = inject(BaseChipDirective, { host: true });

  /** The filterable surface this chip is projected into, if any. */
  private readonly filterHost = inject(FILTER_HOST, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  private readonly _value = signal(false);

  /** The toggle's boolean value. */
  readonly value = computed<unknown>(() => this._value());

  /** @see FilterControl.clearedValue */
  readonly clearedValue = computed<unknown>(() => false);

  /** Whether the toggle is on. */
  readonly active = computed(() => this._value());

  /** @see FilterPresenter.multiple — a toggle holds one boolean. */
  readonly multiple = computed(() => false);

  /** @see FilterPresenter.summary — a toggle has no per-option summary. */
  readonly summary = computed(() => "");

  /** @see FilterPresenter.selections — a toggle has no options; `active` stands in for them. */
  readonly selections = computed<readonly FilterSelection[]>(() => []);

  /** @see FilterPresenter.optionsTemplate — a toggle has no drill-in; it flips in place. */
  readonly optionsTemplate = computed<TemplateRef<unknown> | undefined>(() => undefined);

  /** The displayed icon — {@link iconActive} while active (if supplied), else {@link icon}. */
  protected readonly displayIcon = computed<VowkeyIcon | undefined>(() =>
    this._value() ? (this.iconActive() ?? this.icon()) : this.icon(),
  );

  protected readonly disabled = computed(() => this.baseChip.disabled());

  /** Whether a {@link disabledTooltip} is in play, i.e. the chip is disabled and has a reason. */
  protected readonly showDisabledReason = computed(
    () => this.disabled() && this.disabledTooltip().length > 0,
  );

  /**
   * The chip's tooltip: the disabled reason, and nothing otherwise. The label is already the
   * chip's accessible name, so it gets no tooltip of its own.
   */
  protected readonly chipTooltip = computed(() =>
    this.showDisabledReason() ? this.disabledTooltip() : "",
  );

  constructor() {
    // The base chip defaults to `primary`, which it only draws while selected.
    this.baseChip.variant.set("subtle");
    effect(() => this.baseChip.selectedState.set(this._value()));
  }

  ngOnInit(): void {
    // Register with the host (if any) once inputs like `key` have resolved, not in
    // the constructor: the host seeds initial filters off `key`, which isn't set
    // yet at construction. Inert when there's no host (used outside a table).
    const host = this.filterHost;
    if (!host) {
      return;
    }
    host.registerFilter(this);
    this.destroyRef.onDestroy(() => host.unregisterFilter(this));
  }

  /** Flips the toggle. Wired to the chip click and the responsive dialog's row. */
  flip(): void {
    if (this.disabled()) {
      return;
    }
    this._value.update((v) => !v);
  }

  /** @see FilterPresenter.deselect — a toggle has no values to remove; `clear` turns it off. */
  deselect(): void {
    /* no-op: a toggle has no individual selections. */
  }

  /** @see FilterPresenter.clear */
  clear(): void {
    this._value.set(false);
  }

  setValue(value: unknown): void {
    this._value.set(!!value);
  }
}
